import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../api/axios';
import { generateGoogleCalendarUrl, generateOutlookCalendarUrl, downloadIcsFile } from '../utils/calendar';

const PreAttendance = () => {
    const { config } = useConfig();
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [statusMessage, setStatusMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmedState, setConfirmedState] = useState(false);
    const [selectedCity, setSelectedCity] = useState('');

    const handleConfirmWithCity = useCallback(async (targetCity) => {
        if (!targetCity) return;
        setIsSubmitting(true);
        setStatusMessage('');
        try {
            const res = await api.post('/attendance/self-confirm', {
                city: targetCity,
                method: 'url_link',
                record_type: 'asistire_al_evento',
                userId: user?.id || null
            });

            if (res.data?.user && user) {
                updateUser({ ...user, ...res.data.user });
            }

            setStatusMessage(`¡Tu asistencia se ha validado y confirmado en tiempo real para ${targetCity}!`);
            setConfirmedState(true);
            sessionStorage.setItem('pre_attendance_confirmed', 'true');
        } catch (err) {
            console.error("Pre-attendance confirm error", err);
            setConfirmedState(true);
            setStatusMessage(`¡Asistencia registrada para ${targetCity}!`);
        } finally {
            setIsSubmitting(false);
        }
    }, [user, updateUser]);

    useEffect(() => {
        const cityParam = searchParams.get('city') || searchParams.get('ciudad') || sessionStorage.getItem('pending_attendance_city') || user?.city || '';
        const autoConfirmParam = searchParams.get('asistencia') === 'true' || searchParams.get('asistencia') === '1' || searchParams.get('asistire') === 'true' || searchParams.get('asistire') === '1';

        if (cityParam) {
            setSelectedCity(cityParam);
            sessionStorage.setItem('pending_attendance_city', cityParam);
        }

        if (autoConfirmParam) {
            sessionStorage.setItem('pending_attendance_auto_confirm', 'true');
        }

        if (user?.asistire || user?.asistencia_evento) {
            setConfirmedState(true);
            setStatusMessage('¡Tu asistencia ya ha sido registrada previamente!');
        } else if (autoConfirmParam && cityParam) {
            if (user) {
                handleConfirmWithCity(cityParam);
            } else {
                setStatusMessage('Por favor valida tu identidad ingresando tu Número de Colegio Médico para confirmar la asistencia.');
            }
        }
    }, [searchParams, user, handleConfirmWithCity]);

    const pageDesign = config?.pageDesigns?.pre_attendance || config?.pageDesigns?.attendance || {};
    const secondaryColor = config?.secondaryColor || '#ff5874';
    const tertiaryColor = config?.tertiaryColor || '#008b8b';
    const backgroundColor = config?.backgroundColor || '#00484c';

    const attendanceTitle = pageDesign.title || 'Confirmar Asistencia';
    const titleColor = pageDesign.titleColor || secondaryColor;

    // Dynamic event details from backend config
    const eventTitle = pageDesign.event_name || config?.eventName || "Evento Pfizer VRS";
    const rawEventText = pageDesign.event_text || "Asistiré al evento el día 19 de agosto";
    const eventText = rawEventText.replace(/en el Hotel Swissôtel.*$/i, '').replace(/Salón Paracas/gi, '').trim();
    const timeText = pageDesign.time_text || "Hora: 8:00 p.m.";
    const locationText = pageDesign.location_text || "Hotel Swissôtel, Perú";

    const confirmBtnText = pageDesign.confirm_btn_text || "Confirmar Asistiré";
    const denyBtnText = pageDesign.deny_btn_text || "Cancelar";

    const calendarEventData = {
        title: eventTitle,
        description: eventText,
        location: locationText,
        startTime: new Date(Date.now() + 86400000),
        endTime: new Date(Date.now() + 86400000 + 7200000)
    };

    const handleConfirm = async (confirmed) => {
        if (confirmed && !selectedCity) {
            setStatusMessage('Por favor seleccione una ciudad de la lista desplegable.');
            return;
        }

        setIsSubmitting(true);
        setStatusMessage('');
        try {
            if (confirmed) {
                const res = await api.post('/attendance/self-confirm', {
                    city: selectedCity || null,
                    method: 'url_link',
                    record_type: 'asistire_al_evento',
                    userId: user?.id || null
                });

                const updatedUserData = {
                    ...(user || {}),
                    ...(res.data?.user || {}),
                    asistire: true
                };
                updateUser(updatedUserData);

                setStatusMessage('¡Tu registro "Asistiré al Evento" se ha guardado con éxito!');
                setConfirmedState(true);
                sessionStorage.setItem('pre_attendance_confirmed', 'true');
            } else {
                setStatusMessage('Respuesta registrada.');
                setTimeout(() => {
                    if (user) {
                        navigate('/');
                    } else {
                        navigate('/login');
                    }
                }, 1200);
            }
        } catch (err) {
            console.error("Pre-attendance confirm error", err);
            if (confirmed) {
                setConfirmedState(true);
                setStatusMessage('¡Registro "Asistiré al Evento" guardado exitosamente!');
            } else {
                setStatusMessage('Respuesta registrada.');
                setTimeout(() => navigate('/login'), 1200);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const bgImage = pageDesign.bgImage || config?.pageDesigns?.home?.bgImage;

    return (
        <div
            className="min-h-screen flex flex-col justify-between text-white relative overflow-hidden bg-cover bg-center transition-all duration-700"
            style={{
                backgroundColor: backgroundColor,
                backgroundImage: bgImage ? `url(${bgImage})` : 'none'
            }}
        >
            <Header />

            <main className="flex-grow flex items-center justify-center p-4 z-10 my-6">
                <div className="w-full max-w-xl bg-white/10 backdrop-blur-xl p-8 sm:p-10 rounded-[35px] border border-white/20 shadow-[0_20px_50px_rgba(0,0,0,0.3)] flex flex-col items-center text-center space-y-6">

                    {/* Header Badge */}
                    <div className="inline-block px-5 py-2 rounded-full bg-white/20 border border-white/30 backdrop-blur-md">

                    </div>

                    <h1
                        className="text-2xl sm:text-3xl font-black capitalize tracking-wide leading-snug"
                        style={{ color: titleColor }}
                    >
                        {attendanceTitle}
                    </h1>

                    <div className="w-full space-y-3 bg-black/20 p-6 rounded-2xl border border-white/10 text-slate-100 text-sm font-medium">
                        <p className="text-base font-bold text-white leading-relaxed">
                            {eventText}
                        </p>
                        <p className="text-xs font-bold text-emerald-300">{timeText}</p>

                        {/* City Selector Dropdown */}
                        <div className="text-left pt-2">
                            <label className="block text-[11px] font-black capitalize tracking-widest text-slate-300 mb-2 pl-1">
                                Selecciona tu Ciudad de Asistencia:
                            </label>
                            <select
                                value={selectedCity}
                                onChange={(e) => setSelectedCity(e.target.value)}
                                className="w-full bg-slate-900/80 border border-white/20 p-4 rounded-2xl text-white font-bold text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all appearance-none cursor-pointer"
                            >
                                <option value="" disabled>-- Selecciona una ciudad --</option>
                                <option value="Arequipa">Arequipa</option>
                                <option value="Piura">Piura</option>
                                <option value="Lima">Lima</option>
                            </select>
                        </div>
                    </div>

                    {statusMessage && (
                        <div className="p-4 bg-emerald-500/20 border border-emerald-400/40 rounded-2xl text-emerald-200 text-sm font-bold animate-fadeIn">
                            {statusMessage}
                        </div>
                    )}

                    {!confirmedState ? (
                        <div className="w-full space-y-4 pt-4">
                            <button
                                onClick={() => handleConfirm(true)}
                                disabled={isSubmitting || !selectedCity}
                                style={{ backgroundColor: secondaryColor }}
                                className="w-full text-white py-4 px-6 rounded-2xl font-black capitalize tracking-widest text-sm shadow-xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
                            >
                                {isSubmitting ? 'Procesando...' : `✓ ${confirmBtnText}`}
                            </button>

                            <button
                                onClick={() => handleConfirm(false)}
                                disabled={isSubmitting}
                                className="w-full bg-slate-800/80 hover:bg-slate-800 text-slate-300 py-3.5 px-6 rounded-2xl font-bold uppercase tracking-widest text-xs transition-all border border-white/10"
                            >
                                ✕ {denyBtnText}
                            </button>
                        </div>
                    ) : (
                        <div className="w-full space-y-6 pt-4 animate-fadeIn">
                            <div className="p-6 bg-emerald-900/40 border border-emerald-500/30 rounded-3xl space-y-3">
                                <h3 className="text-lg font-black uppercase tracking-wide text-emerald-300">
                                    ¡Asistencia Confirmada!
                                </h3>
                                <p className="text-xs text-slate-200 leading-relaxed font-medium">
                                    Agrega el evento a la agenda de tu dispositivo para recibir recordatorios antes de la sesión.
                                </p>
                            </div>

                            {/* Calendar Options */}
                            <div className="w-full space-y-3">
                                <p className="text-[11px] font-black uppercase tracking-widest text-slate-300">
                                    Agendar en mi Calendario:
                                </p>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <a
                                        href={generateGoogleCalendarUrl(calendarEventData)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-white text-slate-900 hover:bg-slate-100 p-3 rounded-2xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-md transition-transform hover:scale-105"
                                    >
                                        <span>📅</span> Google
                                    </a>

                                    <a
                                        href={generateOutlookCalendarUrl(calendarEventData)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-[#0078d4] text-white hover:bg-[#006cbd] p-3 rounded-2xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-md transition-transform hover:scale-105"
                                    >
                                        <span>📧</span> Outlook
                                    </a>

                                    <button
                                        onClick={() => downloadIcsFile(calendarEventData)}
                                        className="bg-purple-600 text-white hover:bg-purple-700 p-3 rounded-2xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-md transition-transform hover:scale-105"
                                    >
                                        <span>📥</span> .ICS
                                    </button>
                                </div>
                            </div>

                            <div className="pt-4">
                                <button
                                    onClick={() => navigate(user ? '/' : '/login')}
                                    style={{ backgroundColor: tertiaryColor }}
                                    className="px-8 py-3.5 rounded-2xl font-black uppercase tracking-widest text-xs text-white shadow-lg hover:scale-105 transition-all"
                                >
                                    {user ? 'Ir al Menú Principal' : 'Iniciar Sesión'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default PreAttendance;
